import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Phone } from 'lucide-react';
export function Hero() {
  const [showCopied, setShowCopied] = useState(false);
  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Fire Google Ads conversion event
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;
      (window as any).gtag('event', 'conversion', {
        send_to: 'AW-349742034/M8ECCPrSnrADENLH4qYB'
      });
    }
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
      e.preventDefault();
      const phoneNumber = '(818) 381-9905';
      navigator.clipboard.writeText(phoneNumber).then(() => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      }).catch(() => {
        window.location.href = 'tel:8183819905';
      });
    }
  };
  return <section id="home" className="relative w-full min-h-[80vh] flex flex-col items-center justify-center bg-black overflow-hidden px-4 py-20 pt-40" data-id="element-205" data-mp="381xl8y8vx2a6">
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px] pointer-events-none" data-id="element-206" data-mp="3o7bzrwu0yng3" />

      {/* Logo Container */}
      <div className="relative z-10 mb-12 flex items-center justify-center gap-6 md:gap-10" data-id="element-207" data-mp="3e9fuvmrokoqf">
        {/* Logo */}
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="relative" style={{
        mixBlendMode: 'lighten'
      }} data-id="element-208" data-mp="30hhnphhj52ww">
          <img src="https://cdn.magicpatterns.com/uploads/cLkq5sUtoNy1UGX3WoA69D/Possible_New_Logo_Look_Mikes_Pro_Detail_.png" alt="Mike's Pro Detail mobile auto detailing logo" className="w-full max-w-[400px] md:max-w-[600px] h-auto drop-shadow-2xl" fetchPriority="high" decoding="async" data-id="element-209" data-mp="2viy3blil3z6n" />

          {/* Animated Sparkles overlaying the logo area */}
          <Sparkle className="absolute top-[20%] left-[10%] w-6 h-6 text-white" delay={0.5} data-id="element-210" data-mp="s2tb5evynxmk" />
          <Sparkle className="absolute top-[15%] right-[15%] w-8 h-8 text-white" delay={1.2} data-id="element-211" data-mp="najgjljwi9dy" />
          <Sparkle className="absolute bottom-[30%] left-[5%] w-4 h-4 text-blue-400" delay={2.0} data-id="element-212" data-mp="id9r45wihxq4" />
        </motion.div>
      </div>

      {/* Phone Number - Right below logo */}
      <motion.a href="tel:8183819905" onClick={handlePhoneClick} initial={{
      y: 20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.5
    }} className="relative z-10 inline-flex items-center gap-2 sm:gap-3 mb-6 max-w-full text-[26px] sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter whitespace-nowrap hover:scale-105 transition-transform cursor-pointer" aria-label="Call Mike's Pro Detail" data-id="element-213" data-mp="1b1fez1iv42u6">
        <Phone className="w-6 h-6 sm:w-9 sm:h-9 md:w-12 md:h-12 shrink-0 text-red-500 fill-red-500/20" data-id="element-214" data-mp="3hrd80rmt53yb" />
        (818) 381-9905
      </motion.a>

      {/* Text Content */}
      <div className="relative z-10 text-center space-y-6" data-id="element-215" data-mp="1xb5ped03cpfl">
        <motion.h1 initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter uppercase px-2" data-id="element-216" data-mp="21a4ohgz55rz4">
          <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg font-['DM_Sans',_sans-serif]" data-id="element-217" data-mp="2b2kcaux4darx">
            Mobile Detailing Near You
          </span>
        </motion.h1>

        {/* Vehicle Images - Below "Mobile Detailing Near You" */}
        <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 w-full max-w-5xl mx-auto px-2" data-id="element-218" data-mp="2oyo28c0ymwyl">
          <motion.div initial={{
          x: -40,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-219" data-mp="umzhft81s06a">
            <img src="https://cdn.magicpatterns.com/uploads/bxaw5c4C179AU76JAVR3yB/Red_BMW_.jpg" alt="Red BMW detailed by Mike's Pro Detail" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-220" data-mp="3vgvven7a573m" />
          </motion.div>
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.85,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-221" data-mp="3hphb8d1myzuz">
            <img src="https://cdn.magicpatterns.com/uploads/bNVZWuDfAH9gZxv5SBhs7n/Blue_BMW_SUV.jpg" alt="Blue BMW SUV detailed by Mike's Pro Detail" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-222" data-mp="3mnsn0tbhrkfu" />
          </motion.div>
          <motion.div initial={{
          x: 40,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7,
          ease: 'easeOut'
        }} className="flex-1 min-w-0 max-w-[125px] sm:max-w-[175px] md:max-w-[250px] lg:max-w-[325px]" data-id="element-223" data-mp="19hvn0qrrct9c">
            <img src="https://cdn.magicpatterns.com/uploads/7rUwRJWD2ZeqrnUc9UXcfz/Red_BIG_Truck.jpg" alt="Red truck detailed by Mike's Pro Detail" className="w-full h-auto rounded-lg md:rounded-xl shadow-2xl object-cover border border-zinc-800 md:border-2" data-id="element-224" data-mp="14hhr273fl9ro" />
          </motion.div>
        </div>

        <motion.h2 initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter uppercase px-2" data-id="element-225" data-mp="1jajjtliqmx82">
          <span className="inline-block mt-10 sm:mt-12 px-1 bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg text-[34px] sm:text-[48px] md:text-5xl font-['DM_Sans',_sans-serif]" data-id="element-226" data-mp="bvyarb3z1k5u">
            Ventura County
          </span>
          <br data-id="element-227" data-mp="24rhfmr0ymb4a" />
          <span className="inline-block mt-2 bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg text-[28px] sm:text-[40px] md:text-4xl" data-id="element-228" data-mp="24qz991kl2gin">
            &amp;
          </span>
          <br data-id="element-229" data-mp="1vrubhxguir7j" />
          <span className="inline-block mt-2 bg-clip-text text-transparent bg-gradient-to-b from-white via-gray-200 to-gray-500 drop-shadow-lg text-[30px] sm:text-[44px] md:text-5xl font-['DM_Sans',_sans-serif]" data-id="element-230" data-mp="1vsnimzjpviq7">
            San Fernando Valley
          </span>
        </motion.h2>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="flex flex-col items-center gap-4" data-id="element-231" data-mp="1gv3l2dp5gpu2">
          <p className="text-base sm:text-xl md:text-2xl font-bold text-yellow-400 tracking-wide sm:tracking-widest uppercase drop-shadow-md text-center px-2" data-id="element-232" data-mp="3b8cphr2u9l5g">
            5-Star Rated Auto Detailing Service
            <br data-id="element-233" data-mp="3nolzhfpfdue8" />
            Camarillo, Thousand Oaks, Ventura & All of Ventura County
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl text-center px-2" data-id="element-234" data-mp="3sj8fxxad6d56">
            Professional mobile car detailing at your home or office. No
            drop-off needed.
          </p>

          <a href="#contact" className="mt-8 px-8 py-4 bg-gradient-to-r from-red-600 to-red-800 text-white font-bold text-lg uppercase tracking-wider rounded-sm hover:from-red-500 hover:to-red-700 transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.8)]" data-id="element-235" data-mp="30sc32wob08hw">
            Book Now
          </a>
        </motion.div>
      </div>

      {/* Copied to clipboard toast */}
      {showCopied && <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-236" data-mp="tz9mqqv7bkx8">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-237" data-mp="1b28kv86po4hl">
            <polyline points="20 6 9 17 4 12" data-id="element-238" data-mp="1otx19nnztf4d"></polyline>
          </svg>
          <span className="font-semibold" data-id="element-239" data-mp="1ov7zl7yo5uo3">
            Phone number copied to clipboard!
          </span>
        </div>}
    </section>;
}
function Sparkle({
  className,
  delay
}: {
  className?: string;
  delay: number;
}) {
  return <motion.div initial={{
    scale: 0,
    opacity: 0,
    rotate: 0
  }} animate={{
    scale: [0, 1, 0],
    opacity: [0, 1, 0],
    rotate: [0, 90, 180]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    repeatDelay: 3,
    delay: delay,
    ease: 'easeInOut'
  }} className={className} data-id="element-240" data-mp="1tszw6fa9e4ym">
      <Star fill="currentColor" className="w-full h-full" data-id="element-241" data-mp="ddf2hgwxb0ek" />
    </motion.div>;
}